/**
 * Onboarding (spec 020 US2) — sequenced, one-cue-at-a-time guided onboarding for
 * a surface (the Dashboard, the Editor, …) instead of a wall-of-text first-run
 * modal. Shows exactly ONE cue, advances on "Next" (→ "Done" on the last step),
 * dismisses on "Skip", and persists completion/skip per browser so it never
 * reappears (see ./onboarding seen-state). Steps whose target isn't available
 * (role-gated / unmounted) are skipped. Reduced-motion + tokens honoured in CSS.
 */
import { useMemo, useState } from "react";
import { hasSeenOnboarding, markOnboardingSeen } from "./onboardingState.js";
import "./ui.css";

export interface OnboardingStep {
  /** Optional short heading for the cue. */
  title?: string;
  /** The one thing this cue asks the user to do. */
  body: string;
  /** When false the step is omitted (e.g. its target control is role-gated/unmounted). */
  available?: boolean;
}

export interface OnboardingProps {
  /** Stable id for this surface's sequence; its seen/skipped state is persisted under it. */
  sequenceKey: string;
  steps: OnboardingStep[];
  /** Called once when the sequence is completed or skipped. */
  onDone?: () => void;
}

export function Onboarding({ sequenceKey, steps, onDone }: OnboardingProps) {
  // Resolve the visible steps once per mount (available !== false).
  const visible = useMemo(() => steps.filter((s) => s.available !== false), [steps]);
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(() => hasSeenOnboarding(sequenceKey));

  if (done || visible.length === 0 || index >= visible.length) return null;

  const finish = () => {
    markOnboardingSeen(sequenceKey);
    setDone(true);
    onDone?.();
  };
  const isLast = index === visible.length - 1;
  const step = visible[index];

  return (
    <div className="onboarding-cue" data-testid="onboarding-cue" role="dialog" aria-label="Getting started">
      {step.title && <strong className="onboarding-cue__title">{step.title}</strong>}
      <p className="onboarding-cue__body">{step.body}</p>
      <div className="onboarding-cue__foot">
        <span className="onboarding-cue__count">{index + 1} of {visible.length}</span>
        <span className="onboarding-cue__actions">
          {/* review-1 m-2: both were `--sm`. "Next"/"Done" is the cue card's PRIMARY — not a
              row action, not a toolbar action — and `--sm` is reserved for those. Skip is the
              ghost beside it and moves with it so the pair stays one size. */}
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="onboarding-skip"
            onClick={finish}
          >
            Skip
          </button>
          <button
            type="button"
            className="btn btn--primary"
            data-testid="onboarding-next"
            onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
          >
            {isLast ? "Done" : "Next"}
          </button>
        </span>
      </div>
    </div>
  );
}
